import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import {
  buildDashboardDateParams,
  transformDailyCountsForChart,
} from '../../utils/dashboardChartData';

const LinkStatsChart = ({ dateRange, fromDate = '', rangeLabel }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [dailyCounts, setDailyCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLinkStats = async () => {
      if ((dateRange === 'custom' || dateRange === 'single') && !fromDate) {
        setLoading(false);
        setDailyCounts([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/bid-links/daily-count`,
          {
            params: {
              ...buildDashboardDateParams({ days: dateRange, fromDate }),
              showBlacklisted: 'false',
              showHidden: 'false',
            },
          }
        );
        setDailyCounts(response.data?.dailyCounts || []);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load link stats');
        setDailyCounts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLinkStats();
  }, [dateRange, fromDate]);

  const dateFilter = useMemo(
    () => ({
      days: dateRange,
      fromDate: (dateRange === 'custom' || dateRange === 'single') ? fromDate : '',
    }),
    [dateRange, fromDate]
  );

  const chartData = useMemo(
    () => transformDailyCountsForChart(dailyCounts, dateFilter),
    [dailyCounts, dateFilter]
  );

  const total = useMemo(
    () => chartData.reduce((sum, entry) => sum + entry.count, 0),
    [chartData]
  );

  const tickColor = isDarkMode ? '#ffffff' : '#1f293b';
  const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)';

  return (
    <Paper sx={{ p: 3, color: 'text.primary' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography variant="h6">Bid Links Distribution</Typography>
        <Typography variant="body2" color="text.secondary">
          {total} links in selected period
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Daily link counts use the same filters as the Bid Links page (blacklists, confidence, hidden links excluded).
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : chartData.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No link data for {rangeLabel?.toLowerCase() || 'the selected period'}
        </Typography>
      ) : (
        <Box sx={{ height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="date"
                tick={{ fill: tickColor, fontSize: 11 }}
                interval="preserveStartEnd"
              />
              <YAxis allowDecimals={false} tick={{ fill: tickColor }} />
              <Tooltip
                formatter={(value) => [value, 'Links']}
                labelFormatter={(label) => `Date: ${label}`}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 6, strokeWidth: 2 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default LinkStatsChart;
